import { PrivateRoute, RouteSpinner } from "@/components/auth/PrivateRoute";
import { PublicRoute } from "@/components/auth/PublicRoute";
import { AppShell } from "@/components/layout/AppShell";
import { type ReactNode, Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

/**
 * Every route is code-split. The landing page is the first thing an anonymous
 * visitor downloads, and it has no business shipping the rule builder, recharts
 * or the dashboard's query graph alongside it.
 */
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const OnboardingLayout = lazy(() => import("@/pages/onboarding/OnboardingLayout"));
const OnboardingIntro = lazy(() => import("@/pages/onboarding/OnboardingIntro"));
const ApiKeyStep = lazy(() => import("@/pages/onboarding/ApiKeyStep"));
const AllowIpStep = lazy(() => import("@/pages/onboarding/AllowIpStep"));
const AccountStep = lazy(() => import("@/pages/onboarding/AccountStep"));
const DoneStep = lazy(() => import("@/pages/onboarding/DoneStep"));

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const PerformancePage = lazy(() => import("@/pages/PerformancePage"));
const BookPage = lazy(() => import("@/pages/BookPage"));
const MarketPage = lazy(() => import("@/pages/MarketPage"));
const AssistantPage = lazy(() => import("@/pages/assistant/AssistantPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));

const RulesListPage = lazy(() => import("@/pages/rules/RulesListPage"));
const RuleNewPage = lazy(() => import("@/pages/rules/RuleNewPage"));
const RuleDetailPage = lazy(() => import("@/pages/rules/RuleDetailPage"));
const RuleEditPage = lazy(() => import("@/pages/rules/RuleEditPage"));
const RuleBacktestPage = lazy(() => import("@/pages/rules/RuleBacktestPage"));
const RuleReportPage = lazy(() => import("@/pages/rules/RuleReportPage"));

const SettingsLayout = lazy(() => import("@/pages/settings/SettingsLayout"));
const ApiKeySettingsPage = lazy(() => import("@/pages/settings/ApiKeySettingsPage"));
const McpSettingsPage = lazy(() => import("@/pages/settings/McpSettingsPage"));
const ConnectPage = lazy(() => import("@/pages/ConnectPage"));
const AssistantSettingsPage = lazy(() => import("@/pages/settings/AssistantSettingsPage"));
const AccountSettingsPage = lazy(() => import("@/pages/settings/AccountSettingsPage"));
const LimitsSettingsPage = lazy(() => import("@/pages/settings/LimitsSettingsPage"));
const NotificationSettingsPage = lazy(() => import("@/pages/settings/NotificationSettingsPage"));
const ProfileSettingsPage = lazy(() => import("@/pages/settings/ProfileSettingsPage"));

/** Signed-in pages share the app chrome (nav, connection badge, kill switch). */
function Shell({ children }: { children: ReactNode }) {
  return (
    <PrivateRoute>
      <AppShell>{children}</AppShell>
    </PrivateRoute>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteSpinner />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
        <Route path="/legal/:doc" element={<LegalPage />} />

        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          }
        />

        {/* 키 등록 절차. 게이트가 아니라 대시보드에서 들어오는 별도 흐름이에요. */}
        <Route
          path="/onboarding"
          element={
            <PrivateRoute>
              <OnboardingLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<OnboardingIntro />} />
          <Route path="api-key" element={<ApiKeyStep />} />
          <Route path="allow-ip" element={<AllowIpStep />} />
          <Route path="account" element={<AccountStep />} />
          <Route path="done" element={<DoneStep />} />
        </Route>

        <Route
          path="/dashboard"
          element={
            <Shell>
              <DashboardPage />
            </Shell>
          }
        />
        <Route
          path="/orders"
          element={
            <Shell>
              <OrdersPage />
            </Shell>
          }
        />
        <Route
          path="/performance"
          element={
            <Shell>
              <PerformancePage />
            </Shell>
          }
        />
        <Route
          path="/book"
          element={
            <Shell>
              <BookPage />
            </Shell>
          }
        />
        <Route
          path="/market"
          element={
            <Shell>
              <MarketPage />
            </Shell>
          }
        />
        <Route
          path="/market/:symbol"
          element={
            <Shell>
              <MarketPage />
            </Shell>
          }
        />

        <Route
          path="/assistant"
          element={
            <Shell>
              <AssistantPage />
            </Shell>
          }
        />

        <Route
          path="/rules"
          element={
            <Shell>
              <RulesListPage />
            </Shell>
          }
        />
        <Route
          path="/rules/new"
          element={
            <Shell>
              <RuleNewPage />
            </Shell>
          }
        />
        <Route
          path="/rules/:id"
          element={
            <Shell>
              <RuleDetailPage />
            </Shell>
          }
        />
        <Route
          path="/rules/:id/edit"
          element={
            <Shell>
              <RuleEditPage />
            </Shell>
          }
        />
        <Route
          path="/rules/:id/backtest"
          element={
            <Shell>
              <RuleBacktestPage />
            </Shell>
          }
        />
        {/* `backtestId` 없이 열면 그 규칙의 가장 최근 결과를 씁니다. */}
        <Route
          path="/rules/:id/report"
          element={
            <Shell>
              <RuleReportPage />
            </Shell>
          }
        />
        <Route
          path="/rules/:id/report/:backtestId"
          element={
            <Shell>
              <RuleReportPage />
            </Shell>
          }
        />

        <Route
          path="/settings"
          element={
            <Shell>
              <SettingsLayout />
            </Shell>
          }
        >
          <Route index element={<Navigate to="/settings/api-key" replace />} />
          <Route path="api-key" element={<ApiKeySettingsPage />} />
          <Route path="mcp" element={<McpSettingsPage />} />
          <Route path="assistant" element={<AssistantSettingsPage />} />
          <Route path="account" element={<AccountSettingsPage />} />
          <Route path="limits" element={<LimitsSettingsPage />} />
          <Route path="notifications" element={<NotificationSettingsPage />} />
          <Route path="profile" element={<ProfileSettingsPage />} />
        </Route>

        {/* MCP 연결 동의 — AI 도구가 연 탭이 `/oauth/authorize` 를 거쳐 와요. 앱 크롬 없이 로그인만 봐요. */}
        <Route
          path="/connect"
          element={
            <PrivateRoute>
              <ConnectPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute requireAdmin>
              <AppShell>
                <AdminPage />
              </AppShell>
            </PrivateRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
